import type { AdminRole } from "./user";

/**
 * Who may do what in the back office. One table, read by the API (which enforces it) and the
 * admin app (which hides what a role can't use). Rules use CASL's raw-rule shape, so both sides
 * build their ability straight from it: `createMongoAbility(ROLE_PERMISSIONS[role])`.
 */

export const PERMISSION_ACTIONS = ["manage", "read", "create", "update", "delete"] as const;
/** "manage" means every action. */
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

/** One subject per area of the admin app. "all" means every subject. */
export const PERMISSION_SUBJECTS = [
  "all",
  "Dashboard",
  "Order",
  "Subscription",
  "Customer",
  "Ticket",
  "HelpFaq",
  "Product",
  "Category",
  "Farmer",
  "Media",
  "Inventory",
  "Delivery",
  "Coupon",
  "Banner",
  "Recommendation",
  "Announcement",
  "Campaign",
  "Refund",
  "Wallet",
  "Invoice",
  "Settings",
  "AdminUser",
] as const;
export type PermissionSubject = (typeof PERMISSION_SUBJECTS)[number];

export interface PermissionRule {
  action: PermissionAction | PermissionAction[];
  subject: PermissionSubject | PermissionSubject[];
  /** A "cannot" rule; later rules win over earlier ones. */
  inverted?: boolean;
  /** Shown to the user when this rule is what refused them. */
  reason?: string;
}

/** Everything a support agent can look at. */
const OPERATIONS: PermissionSubject[] = [
  "Dashboard",
  "Order",
  "Subscription",
  "Customer",
  "Ticket",
  "HelpFaq",
  "Product",
  "Category",
  "Farmer",
  "Media",
  "Inventory",
  "Delivery",
  "Coupon",
  "Banner",
  "Recommendation",
  "Announcement",
  "Campaign",
  "Refund",
  "Invoice",
];

export const ROLE_PERMISSIONS: Record<AdminRole, PermissionRule[]> = {
  /** Runs the business: everything, including staff, settings and wallet credits. */
  owner: [{ action: "manage", subject: "all" }],

  /** Runs the day: every operational screen, but not money held for customers or the setup. */
  manager: [
    { action: "manage", subject: "all" },
    {
      action: ["create", "update", "delete"],
      subject: ["Wallet", "Settings"],
      inverted: true,
      reason: "Only an owner can change wallet balances or store settings.",
    },
    {
      action: "manage",
      subject: "AdminUser",
      inverted: true,
      reason: "Only an owner can manage staff accounts.",
    },
  ],

  /** Helps customers: sees everything operational, moves orders and drops, answers tickets and keeps
   * the help centre's answers up to date. */
  support: [
    { action: "read", subject: OPERATIONS },
    { action: "update", subject: ["Order", "Delivery"] },
    { action: "manage", subject: ["Ticket", "HelpFaq"] },
    { action: "create", subject: "Refund" },
  ],
};

/** Plural nouns for messages such as "Support can't update products." */
export const PERMISSION_SUBJECT_LABELS: Record<PermissionSubject, string> = {
  all: "everything",
  Dashboard: "the dashboard",
  Order: "orders",
  Subscription: "milk plans",
  Customer: "customers",
  Ticket: "support tickets",
  HelpFaq: "help answers",
  Product: "products",
  Category: "categories",
  Farmer: "farmers",
  Media: "the media library",
  Inventory: "stock",
  Delivery: "deliveries",
  Coupon: "coupons",
  Banner: "banners",
  Recommendation: "product recommendations",
  Announcement: "announcements",
  Campaign: "notifications",
  Refund: "refunds",
  Wallet: "wallet balances",
  Invoice: "invoices",
  Settings: "store settings",
  AdminUser: "staff accounts",
};
